/**
 * LYT Communications - Centralized Theme System
 * Version: 1.0.0
 * Created: 2026-03-01
 *
 * Single source of truth for colors, style generators, and responsive hooks.
 * Import getTheme(darkMode) in any component to get the full palette.
 */

import { useState, useEffect } from 'react';

/**
 * Returns the full color palette for the given theme mode.
 *
 * Two accent families:
 *  - portal: used by dashboards, portals, workflow pages
 *  - marketing: used by HomePage, AboutPage, ServicesPage, ContactPage, App.js header
 */
export function getTheme(darkMode) {
  return {
    colors: {
      // Backgrounds
      bg: darkMode ? '#0d1b2a' : '#ffffff',
      bgAlt: darkMode ? '#0a1628' : '#f8fafc',
      cardBg: darkMode ? '#1e293b' : '#ffffff',
      cardBgAlt: darkMode ? '#112240' : '#f8fafc',
      cardBgDeep: darkMode ? '#1a2f4e' : '#ffffff',

      // Text
      text: darkMode ? '#ffffff' : '#1e293b',
      textMuted: darkMode ? '#8892b0' : '#64748b',
      textFaint: darkMode ? 'rgba(255,255,255,0.5)' : '#94a3b8',
      textMedium: darkMode ? 'rgba(255,255,255,0.6)' : '#6b7280',
      textLight: darkMode ? 'rgba(255,255,255,0.7)' : '#64748b',
      textOnAccent: '#ffffff',

      // Borders
      border: darkMode ? '#374151' : '#e5e7eb',
      borderLight: darkMode ? '#1e3a5f' : '#e2e8f0',
      borderSubtle: darkMode ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.05)',
      borderCard: darkMode ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.05)',

      // Portal accents (dashboards, portals, workflow)
      accentPrimary: darkMode ? '#667eea' : '#00b4d8',
      accentSecondary: darkMode ? '#ff6b35' : '#28a745',
      accentHighlight: darkMode ? '#c850c0' : '#0077B6',

      // Marketing accents (public pages, App.js header/footer)
      accentMarketing: darkMode ? '#c850c0' : '#0077B6',
      accentMarketingAlt: darkMode ? '#ff6b35' : '#00b4d8',
      accentMarketingTertiary: darkMode ? '#e85a4f' : '#28a745',

      // Gradients
      gradient: darkMode
        ? 'linear-gradient(135deg, #667eea 0%, #c850c0 50%, #ff6b35 100%)'
        : 'linear-gradient(135deg, #0077B6 0%, #00b4d8 50%, #28a745 100%)',
      gradientPortal: darkMode
        ? 'linear-gradient(135deg, #667eea, #c850c0)'
        : 'linear-gradient(135deg, #0077B6, #00b4d8)',
      gradientMarketing: darkMode
        ? 'linear-gradient(135deg, #c850c0 0%, #ff6b35 100%)'
        : 'linear-gradient(135deg, #0077B6 0%, #00b4d8 100%)',

      // Semantic
      error: darkMode ? '#ff6b6b' : '#e85a4f',
      warning: '#FFB800',
      success: '#4CAF50',
      info: '#2196F3',

      // Input fields
      inputBg: darkMode ? '#0d1b2a' : '#ffffff',
      inputBorder: darkMode ? '#374151' : '#ddd',
      inputText: '#1f2937',

      // Logo
      logoLY: darkMode ? '#e6c4d9' : '#0a3a7d',
      logoT: darkMode ? '#e6c4d9' : '#2ec7c0',
      logoComm: darkMode ? '#ffffff' : '#1e293b',

      // Top bar
      topBarBg: darkMode ? '#112240' : '#f1f5f9',
      topBarText: darkMode ? 'rgba(255,255,255,0.8)' : '#64748b',
    },
  };
}

/**
 * Standard button style generator.
 * Variants: 'primary', 'secondary', 'outline', 'danger', 'ghost'
 */
export function getButtonStyle(variant, darkMode) {
  const theme = getTheme(darkMode);
  const { colors } = theme;

  const base = {
    padding: '10px 20px',
    borderRadius: '8px',
    fontSize: '0.9rem',
    fontWeight: 600,
    cursor: 'pointer',
    transition: 'background-color 0.2s, opacity 0.2s',
    border: 'none',
    color: '#fff',
  };

  switch (variant) {
    case 'primary':
      return { ...base, backgroundColor: colors.accentPrimary };
    case 'secondary':
      return { ...base, backgroundColor: colors.accentSecondary };
    case 'outline':
      return {
        ...base,
        backgroundColor: 'transparent',
        border: `1px solid ${colors.accentPrimary}`,
        color: colors.accentPrimary,
      };
    case 'danger':
      return { ...base, backgroundColor: colors.error };
    case 'ghost':
      return {
        ...base,
        backgroundColor: 'transparent',
        color: colors.textMuted,
        border: 'none',
      };
    case 'marketing':
      return { ...base, backgroundColor: colors.accentMarketing };
    default:
      return { ...base, backgroundColor: colors.accentPrimary };
  }
}

/**
 * Standard input field style.
 */
export function getInputStyle(darkMode) {
  const theme = getTheme(darkMode);
  const { colors } = theme;

  return {
    width: '100%',
    padding: '10px 14px',
    fontSize: '0.9rem',
    border: `1px solid ${colors.border}`,
    borderRadius: '8px',
    backgroundColor: colors.inputBg,
    color: colors.text,
    boxSizing: 'border-box',
    outline: 'none',
  };
}

/**
 * Standard card container style.
 */
export function getCardStyle(darkMode) {
  const theme = getTheme(darkMode);
  const { colors } = theme;

  return {
    backgroundColor: colors.cardBg,
    borderRadius: '12px',
    padding: '20px',
    border: `1px solid ${colors.borderLight}`,
  };
}

/**
 * Hook: returns true when viewport width < 768px.
 */
export function useMobile(breakpoint = 768) {
  const [isMobile, setIsMobile] = useState(
    typeof window !== 'undefined' ? window.innerWidth < breakpoint : false
  );

  useEffect(() => {
    const check = () => setIsMobile(window.innerWidth < breakpoint);
    window.addEventListener('resize', check);
    return () => window.removeEventListener('resize', check);
  }, [breakpoint]);

  return isMobile;
}
